import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dumbbell, Zap, Activity, Sparkles, Play, RotateCw, Loader2, Wrench, Plus, X, Search, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHASE_DEFAULTS = {
  1: { categories: ["stamina"], difficulty: "beginner", name: "Structural Integrity", color: "blue" },
  2: { categories: ["stamina", "explosiveness"], difficulty: "intermediate", name: "Force Production", color: "purple" },
  3: { categories: ["explosiveness"], difficulty: "intermediate", name: "Explosive Power", color: "orange" },
  4: { categories: ["agility", "stamina"], difficulty: "advanced", name: "Game Conditioning", color: "green" },
  5: { categories: ["agility", "stamina", "explosiveness"], difficulty: "advanced", name: "Elite Conditioning", color: "red" },
};

const CATEGORY_META = {
  stamina: { label: "Stamina", icon: Activity, color: "text-cyan-400" },
  explosiveness: { label: "Explosiveness", icon: Zap, color: "text-orange-400" },
  agility: { label: "Agility", icon: Dumbbell, color: "text-green-400" },
};

const sample = (arr, n) => {
  const shuffled = [...arr].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, n);
};

const WorkoutGenerator = ({ open, onOpenChange, initialPhase = 1 }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("auto"); // "auto" | "custom"
  const [allDrills, setAllDrills] = useState([]);
  const [loadingDrills, setLoadingDrills] = useState(false);

  // Auto-generate state
  const [phase, setPhase] = useState(initialPhase);
  const [category, setCategory] = useState("auto");
  const [difficulty, setDifficulty] = useState("auto");
  const [drillCount, setDrillCount] = useState(4);
  const [generating, setGenerating] = useState(false);
  const [workout, setWorkout] = useState(null);

  // Custom-build state
  const [customSets, setCustomSets] = useState(3);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterDifficulty, setFilterDifficulty] = useState("all");
  const [pickedIds, setPickedIds] = useState([]); // array of drill IDs

  // Reset on open
  useEffect(() => {
    if (open) {
      setPhase(initialPhase);
      setWorkout(null);
      setPickedIds([]);
    }
  }, [initialPhase, open]);

  // Fetch drills once when the modal opens
  useEffect(() => {
    if (!open || allDrills.length > 0) return;
    const load = async () => {
      setLoadingDrills(true);
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/drills`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAllDrills(res.data || []);
      } catch (e) {
        console.error("Failed to load drills:", e);
        toast.error("Could not load drills. Try again.");
      } finally {
        setLoadingDrills(false);
      }
    };
    load();
  }, [open, allDrills.length]);

  const generate = async () => {
    setGenerating(true);
    setWorkout(null);
    try {
      const phaseDef = PHASE_DEFAULTS[phase] || PHASE_DEFAULTS[1];
      const wantedCategories = category === "auto" ? phaseDef.categories : [category];
      const wantedDifficulty = difficulty === "auto" ? phaseDef.difficulty : difficulty;

      let pool = allDrills.filter((d) => wantedCategories.includes(d.category));
      const narrowed = pool.filter((d) => d.difficulty === wantedDifficulty);
      if (narrowed.length >= drillCount) pool = narrowed;

      if (pool.length === 0) {
        toast.error("No drills match your filters. Try different settings.");
        return;
      }

      const picked = sample(pool, drillCount);
      const setsByPhase = { 1: 2, 2: 3, 3: 3, 4: 4, 5: 4 };
      const sets = setsByPhase[phase] || 3;

      setWorkout({
        phase,
        phaseName: phaseDef.name,
        categories: wantedCategories,
        difficulty: wantedDifficulty,
        sets,
        drills: picked.map((d) => ({ ...d, sets, rest_seconds: d.rest_seconds || 60 })),
        estimatedMinutes: picked.reduce(
          (acc, d) => acc + Math.ceil((((d.duration_seconds || 60) + (d.rest_seconds || 60)) * sets) / 60),
          0
        ),
        source: "auto",
      });
      toast.success("Workout generated!");
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate workout. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  // Filtered drills for the custom picker
  const filteredDrills = useMemo(() => {
    return allDrills.filter((d) => {
      if (filterCategory !== "all" && d.category !== filterCategory) return false;
      if (filterDifficulty !== "all" && d.difficulty !== filterDifficulty) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const name = (d.name || "").toLowerCase();
        const desc = (d.description || "").toLowerCase();
        if (!name.includes(q) && !desc.includes(q)) return false;
      }
      return true;
    });
  }, [allDrills, filterCategory, filterDifficulty, search]);

  const togglePick = (drillId) => {
    setPickedIds((prev) =>
      prev.includes(drillId) ? prev.filter((id) => id !== drillId) : [...prev, drillId]
    );
  };

  const buildCustomWorkout = () => {
    if (pickedIds.length === 0) {
      toast.error("Pick at least 1 drill to build a workout.");
      return;
    }
    const picked = pickedIds
      .map((id) => allDrills.find((d) => d.id === id))
      .filter(Boolean)
      .map((d) => ({ ...d, sets: customSets, rest_seconds: d.rest_seconds || 60 }));

    const estimatedMinutes = picked.reduce(
      (acc, d) => acc + Math.ceil((((d.duration_seconds || 60) + (d.rest_seconds || 60)) * customSets) / 60),
      0
    );

    setWorkout({
      phase: null,
      phaseName: "Custom Build",
      categories: [...new Set(picked.map((d) => d.category))],
      difficulty: picked[0]?.difficulty || "mixed",
      sets: customSets,
      drills: picked,
      estimatedMinutes,
      source: "custom",
    });
    toast.success(`Custom workout built — ${picked.length} drills`);
  };

  const startInEngine = (drill) => {
    onOpenChange(false);
    navigate("/engine", { state: { selectedDrill: drill } });
  };

  const startFullWorkout = () => {
    onOpenChange(false);
    navigate("/engine", { state: { customWorkout: workout } });
  };

  const resetWorkout = () => {
    setWorkout(null);
    if (mode === "custom") setPickedIds([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-background border-primary/30" data-testid="workout-generator-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            WORKOUT BUILDER
          </DialogTitle>
          <DialogDescription>
            Let the app build it for you — or pick your own drills.
          </DialogDescription>
        </DialogHeader>

        {/* MODE TOGGLE */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-zinc-900 border border-zinc-800 rounded-sm" data-testid="generator-mode-toggle">
          <button
            onClick={() => { setMode("auto"); resetWorkout(); }}
            className={`flex items-center justify-center gap-2 py-3 font-heading text-sm uppercase tracking-wider transition-all ${
              mode === "auto" ? "bg-primary text-white" : "text-zinc-400 hover:text-white"
            }`}
            data-testid="mode-auto-btn"
          >
            <Sparkles className="w-4 h-4" />
            Generate For Me
          </button>
          <button
            onClick={() => { setMode("custom"); resetWorkout(); }}
            className={`flex items-center justify-center gap-2 py-3 font-heading text-sm uppercase tracking-wider transition-all ${
              mode === "custom" ? "bg-primary text-white" : "text-zinc-400 hover:text-white"
            }`}
            data-testid="mode-custom-btn"
          >
            <Wrench className="w-4 h-4" />
            Build My Own
          </button>
        </div>

        {/* AUTO MODE */}
        {mode === "auto" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-4">
              <div>
                <label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Phase</label>
                <Select value={String(phase)} onValueChange={(v) => setPhase(Number(v))}>
                  <SelectTrigger data-testid="generator-phase-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((p) => (
                      <SelectItem key={p} value={String(p)}>Phase {p}: {PHASE_DEFAULTS[p].name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="generator-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (Phase Default)</SelectItem>
                    <SelectItem value="stamina">Stamina</SelectItem>
                    <SelectItem value="explosiveness">Explosiveness</SelectItem>
                    <SelectItem value="agility">Agility</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Difficulty</label>
                <Select value={difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger data-testid="generator-difficulty-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (Phase Default)</SelectItem>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-muted-foreground mb-1 block"># Drills</label>
                <Select value={String(drillCount)} onValueChange={(v) => setDrillCount(Number(v))}>
                  <SelectTrigger data-testid="generator-count-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 drills (~20 min)</SelectItem>
                    <SelectItem value="4">4 drills (~30 min)</SelectItem>
                    <SelectItem value="5">5 drills (~40 min)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={generate}
              disabled={generating || loadingDrills}
              className="w-full bg-primary hover:bg-primary/90 font-heading text-base h-12"
              data-testid="generate-workout-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> GENERATING...</>
              ) : workout ? (
                <><RotateCw className="w-4 h-4 mr-2" /> REGENERATE</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> GENERATE WORKOUT</>
              )}
            </Button>
          </>
        )}

        {/* CUSTOM MODE */}
        {mode === "custom" && (
          <>
            {/* Picked Drills Summary */}
            <div className="bg-zinc-900 border border-zinc-800 p-3 rounded-sm flex items-center justify-between" data-testid="custom-picked-summary">
              <div className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-primary" />
                <span className="text-zinc-300 font-bold">
                  {pickedIds.length} drill{pickedIds.length !== 1 ? "s" : ""} picked
                </span>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs uppercase font-bold text-muted-foreground">Sets:</label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={customSets}
                  onChange={(e) => setCustomSets(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                  className="w-16 h-8 text-center bg-zinc-950 border-zinc-700"
                  data-testid="custom-sets-input"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative md:col-span-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Search drills..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-zinc-950 border-zinc-700"
                  data-testid="custom-search-input"
                />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger data-testid="custom-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="stamina">Stamina</SelectItem>
                  <SelectItem value="explosiveness">Explosiveness</SelectItem>
                  <SelectItem value="agility">Agility</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
                <SelectTrigger data-testid="custom-difficulty-filter"><SelectValue placeholder="Difficulty" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Difficulties</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Drill List */}
            <div className="max-h-72 overflow-y-auto border border-zinc-800 rounded-sm divide-y divide-zinc-900" data-testid="custom-drill-list">
              {loadingDrills && (
                <div className="p-6 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading drills...</div>
              )}
              {!loadingDrills && filteredDrills.length === 0 && (
                <div className="p-6 text-center text-zinc-500 text-sm">No drills match your filters.</div>
              )}
              {!loadingDrills && filteredDrills.map((d) => {
                const meta = CATEGORY_META[d.category] || CATEGORY_META.stamina;
                const Icon = meta.icon;
                const isPicked = pickedIds.includes(d.id);
                return (
                  <button
                    key={d.id}
                    onClick={() => togglePick(d.id)}
                    className={`w-full text-left p-3 flex items-center gap-3 transition-all ${
                      isPicked ? "bg-primary/10 border-l-2 border-primary" : "hover:bg-zinc-900/50 border-l-2 border-transparent"
                    }`}
                    data-testid={`custom-drill-${d.id}`}
                  >
                    <div className={`w-7 h-7 flex items-center justify-center border ${
                      isPicked ? "bg-primary border-primary text-white" : "bg-zinc-950 border-zinc-700 text-zinc-500"
                    }`}>
                      {isPicked ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </div>
                    <Icon className={`w-4 h-4 flex-shrink-0 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{d.name}</p>
                      <p className="text-xs text-zinc-500 truncate">
                        <span className={meta.color}>{meta.label}</span> · {d.difficulty} · {d.duration_seconds || 60}s work
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            <Button
              onClick={buildCustomWorkout}
              disabled={pickedIds.length === 0}
              className="w-full bg-primary hover:bg-primary/90 font-heading text-base h-12"
              data-testid="build-custom-workout-btn"
            >
              <Wrench className="w-4 h-4 mr-2" />
              BUILD WORKOUT ({pickedIds.length})
            </Button>
          </>
        )}

        {/* GENERATED / BUILT WORKOUT DISPLAY */}
        {workout && (
          <div className="space-y-3 mt-4" data-testid="generated-workout">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-primary/20 text-primary font-bold uppercase">
                {workout.source === "custom" ? "Custom Build" : `Phase ${workout.phase}: ${workout.phaseName}`}
              </Badge>
              <Badge variant="outline" className="font-bold">{workout.drills.length} drills</Badge>
              <Badge variant="outline" className="font-bold">{workout.sets} sets each</Badge>
              {workout.difficulty !== "mixed" && (
                <Badge variant="outline" className="font-bold uppercase">{workout.difficulty}</Badge>
              )}
              <Badge variant="outline" className="font-bold">~{workout.estimatedMinutes} min total</Badge>
            </div>

            {workout.drills.map((d, idx) => {
              const meta = CATEGORY_META[d.category] || CATEGORY_META.stamina;
              const Icon = meta.icon;
              return (
                <Card key={d.id || `drill-${idx}`} className="bg-card border-border p-4 hover:border-primary/50 transition-all" data-testid={`generated-drill-${idx}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className={`w-4 h-4 ${meta.color}`} />
                        <span className={`text-xs font-bold uppercase ${meta.color}`}>{meta.label}</span>
                        <Badge variant="outline" className="text-xs uppercase font-bold">{d.difficulty}</Badge>
                      </div>
                      <h4 className="font-heading text-lg font-bold mb-1">{idx + 1}. {d.name}</h4>
                      {d.description && <p className="text-xs text-muted-foreground mb-2">{d.description}</p>}
                      <div className="flex flex-wrap gap-3 text-xs font-mono">
                        <span><span className="text-muted-foreground">Sets:</span> <strong>{d.sets}</strong></span>
                        <span><span className="text-muted-foreground">Work:</span> <strong>{d.duration_seconds || 60}s</strong></span>
                        <span><span className="text-muted-foreground">Rest:</span> <strong>{d.rest_seconds}s</strong></span>
                        {d.basketball_benefit && (
                          <span className="text-cyan-400 italic">🏀 {d.basketball_benefit}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        onClick={() => startInEngine(d)}
                        className="bg-primary hover:bg-primary/90 font-bold whitespace-nowrap"
                        data-testid={`start-drill-${idx}-btn`}
                      >
                        <Play className="w-3 h-3 mr-1" /> START
                      </Button>
                      {workout.source === "custom" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setPickedIds((prev) => prev.filter((id) => id !== d.id));
                            setWorkout((prev) => ({
                              ...prev,
                              drills: prev.drills.filter((x) => x.id !== d.id),
                            }));
                          }}
                          className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                          data-testid={`remove-drill-${idx}-btn`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}

            <Button
              onClick={startFullWorkout}
              className="w-full font-heading h-11 bg-primary hover:bg-primary/90 mt-2"
              data-testid="start-full-workout-btn"
            >
              <Play className="w-4 h-4 mr-2" /> START FULL WORKOUT IN ENGINE →
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkoutGenerator;
